/* eslint-disable max-len */
/* eslint-disable import/prefer-default-export */
import {
  useQueryClient,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  getEtaiement,
  getEtaiements,
  getEtaiementsList,
  createEtaiement,
  updateEtaiement,
  updateEtaiementTransport,
  createEtaiementPhase,
  updateEtaiementPhase,
  deleteEtaiementPhase,
  duplicateEtaiementPhase,
  reorderEtaiementPhases,
  deleteEtaiement,
  sendEtaiement,
  createEtaiementVersion,
  duplicateEtaiement,
  addEtaiementNote,
  updateEtaiementNote,
  deleteEtaiementNote,
  addEtaiementFile,
  deleteEtaiementFile,
} from '~/api/etaiements';
import type { ArgsGetEtaiements, IEtaiement, INote } from '~/types/etaiements';
import type { IPhase } from '~/types/estimates';
import type { IMetaPagination } from '~/types/global';

const useGetEtaiements = (params: ArgsGetEtaiements) => useQuery({
  queryKey: ['etaiements', params],
  queryFn: async (): Promise<{ etaiements: IEtaiement[]; meta?: IMetaPagination } | null> => getEtaiements(params),
  keepPreviousData: true,
});

const useGetEtaiementsList = () => useQuery({
  queryKey: ['etaiements-list'],
  queryFn: async (): Promise<any | null> => getEtaiementsList(),
  refetchOnMount: 'always',
});

const useGetEtaiement = (id: string) => useQuery({
  queryKey: ['etaiement', id],
  queryFn: async (): Promise<IEtaiement | null> => getEtaiement(id),
  keepPreviousData: false,
});

const useCreateEtaiement = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<IEtaiement>) => createEtaiement(data),
    onSuccess: async ({ etaiement }) => {
      queryClient.invalidateQueries({ queryKey: ['etaiements'], exact: false });
      queryClient.setQueryData(['etaiement', etaiement._id], etaiement);
      if (successMessage) toast(successMessage);
    },
  });
};

const useUpdateEtaiement = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<IEtaiement>) => updateEtaiement(data),
    onSuccess: async ({ etaiement }) => {
      queryClient.invalidateQueries({ queryKey: ['etaiements'], exact: false });
      queryClient.setQueryData(['etaiement', etaiement._id], etaiement);
      if (successMessage) toast(successMessage);
    },
  });
};

const useUpdateEtaiementTransport = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { etaiementId: string }) => updateEtaiementTransport(data),
    onSuccess: async ({ etaiement }) => {
      queryClient.setQueryData(['etaiement', etaiement._id], etaiement);
      if (successMessage) toast(successMessage);
    },
  });
};

const useCreateEtaiementPhase = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { etaiementId: string }) => createEtaiementPhase(data),
    onSuccess: async ({ etaiement }: { etaiement: IEtaiement }) => {
      queryClient.invalidateQueries({ queryKey: ['etaiements'], exact: false });
      queryClient.setQueryData(['etaiement', etaiement._id], etaiement);
      if (successMessage) toast(successMessage);
    },
  });
};

const useUpdateEtaiementPhase = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<IPhase> & { etaiementId: string }) => updateEtaiementPhase(data),
    onSuccess: async ({ etaiement }) => {
      queryClient.invalidateQueries({ queryKey: ['etaiements'], exact: false });
      queryClient.setQueryData(['etaiement', etaiement._id], etaiement);
      if (successMessage) toast(successMessage);
    },
  });
};

const useDeleteEtaiementPhase = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { phaseId: string, etaiementId: string }) => deleteEtaiementPhase(data),
    onSuccess: async ({ etaiement }) => {
      queryClient.setQueryData(['etaiement', etaiement._id], etaiement);
      if (successMessage) toast(successMessage);
    },
  });
};

const useDuplicateEtaiementPhase = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { phaseId: string, etaiementId: string }) => duplicateEtaiementPhase(data),
    onSuccess: async ({ etaiement }) => {
      queryClient.invalidateQueries({ queryKey: ['etaiements'], exact: false });
      queryClient.setQueryData(['etaiement', etaiement._id], etaiement);
      if (successMessage) toast(successMessage);
    },
  });
};

const useReorderEtaiementPhases = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { etaiementId: string; phaseIds: string[] }) => reorderEtaiementPhases(data),
    onSuccess: async ({ etaiement }) => {
      queryClient.setQueryData(['etaiement', etaiement._id], etaiement);
    },
    onError: () => {
      toast.error('Impossible de réorganiser les phases');
    },
  });
};

const useDeleteEtaiement = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { etaiementId: string }) => deleteEtaiement(data),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['etaiements'], exact: false });
      if (successMessage) toast(successMessage);
    },
  });
};

const useSendEtaiement = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sendEtaiement(id),
    onSuccess: async ({ etaiement }) => {
      queryClient.invalidateQueries({ queryKey: ['etaiements'], exact: false });
      queryClient.setQueryData(['etaiement', etaiement._id], etaiement);
      if (successMessage) toast(successMessage);
    },
  });
};

const useCreateEtaiementVersion = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => createEtaiementVersion(id),
    onSuccess: async ({ etaiement }) => {
      queryClient.invalidateQueries({ queryKey: ['etaiements'], exact: false });
      queryClient.setQueryData(['etaiement', etaiement._id], etaiement);
      if (successMessage) toast(successMessage);
    },
  });
};

const useDuplicateEtaiement = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { etaiementId: string; clientId: string; }) => duplicateEtaiement(data),
    onSuccess: async ({ etaiement }) => {
      queryClient.invalidateQueries({ queryKey: ['etaiements'], exact: false });
      queryClient.setQueryData(['etaiement', etaiement._id], etaiement);
      if (successMessage) toast(successMessage);
      return etaiement;
    },
  });
};

const useAddEtaiementNote = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<INote> & { documentId: string }) => addEtaiementNote(data),
    onSuccess: async ({ etaiement }) => {
      queryClient.setQueryData(['etaiement', etaiement._id], etaiement);
      if (successMessage) toast(successMessage);
    },
  });
};

const useUpdateEtaiementNote = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<INote> & { documentId: string }) => updateEtaiementNote(data),
    onSuccess: async ({ etaiement }) => {
      queryClient.setQueryData(['etaiement', etaiement._id], etaiement);
      if (successMessage) toast(successMessage);
    },
  });
};

const useDeleteEtaiementNote = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { noteId: string, etaiementId: string }) => deleteEtaiementNote(data),
    onSuccess: async ({ etaiement }) => {
      queryClient.setQueryData(['etaiement', etaiement._id], etaiement);
      if (successMessage) toast(successMessage);
    },
  });
};

const useAddEtaiementFile = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { _id: string, file: File }) => addEtaiementFile(data),
    onSuccess: async ({ etaiement }) => {
      queryClient.invalidateQueries({ queryKey: ['etaiements'], exact: false });
      queryClient.setQueryData(['etaiement', etaiement._id], etaiement);
      if (successMessage) toast(successMessage);
    },
  });
};

const useDeleteEtaiementFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { fileId: string, etaiementId: string }) => deleteEtaiementFile(data),
    onSuccess: async ({ etaiement }) => {
      queryClient.invalidateQueries({ queryKey: ['etaiements'], exact: false });
      queryClient.setQueryData(['etaiement', etaiement._id], etaiement);
    },
  });
};

export {
  useGetEtaiements,
  useGetEtaiementsList,
  useGetEtaiement,
  useCreateEtaiement,
  useUpdateEtaiement,
  useUpdateEtaiementTransport,
  useCreateEtaiementPhase,
  useUpdateEtaiementPhase,
  useDeleteEtaiementPhase,
  useDuplicateEtaiementPhase,
  useReorderEtaiementPhases,
  useDeleteEtaiement,
  useSendEtaiement,
  useCreateEtaiementVersion,
  useDuplicateEtaiement,
  useAddEtaiementNote,
  useUpdateEtaiementNote,
  useDeleteEtaiementNote,
  useAddEtaiementFile,
  useDeleteEtaiementFile,
};
